/**
 * Reading Fansly's payloads (docs/fansly_crm_behaviour_spec.md §1.4, §2.1).
 *
 * Fansly answers `{success, response}`; collections come as arrays or as
 * id-keyed maps (`{"<id>": {...}}`) and are joined to their accounts and
 * media through `aggregationData`. Ids are snowflakes: always strings here.
 * Money is integer thousandths of a dollar. Times are epoch seconds on
 * messages and notifications and milliseconds elsewhere.
 */

export type Raw = Record<string, unknown>

export const asObj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {})

export const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' && Number.isFinite(v) ? String(v) : '')

export const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN
  return Number.isFinite(n) ? n : 0
}

/** `response` of a Fansly body (or `data`), else the body itself. */
export function unwrap(p: unknown): unknown {
  const o = asObj(p)
  if ('response' in o) return o.response
  if ('data' in o && ('ok' in o || 'success' in o)) return o.data
  return p
}

/** An array, or an id-keyed map as an array (the key becomes `id` when the row has none). */
export function asList(v: unknown): Raw[] {
  if (Array.isArray(v)) return v.filter((x) => x && typeof x === 'object') as Raw[]
  if (!v || typeof v !== 'object') return []
  return Object.entries(v as Raw)
    .filter(([, x]) => x && typeof x === 'object' && !Array.isArray(x))
    .map(([key, x]) => {
      const row = x as Raw
      return row.id != null || row.accountMediaId != null || row.bundleId != null || row.postId != null ? row : { ...row, id: key }
    })
}

/** The rows of a payload: the payload itself when it is a list, else the first list under `keys`. */
export function rows(p: unknown, keys: readonly string[]): Raw[] {
  const u = unwrap(p)
  if (Array.isArray(u)) return asList(u)
  const o = asObj(u)
  for (const k of keys) {
    const v = o[k]
    if (Array.isArray(v) || (v && typeof v === 'object' && Object.keys(v as Raw).length && Object.keys(v as Raw).every((x) => /^\d+$/.test(x)))) {
      return asList(v)
    }
  }
  return []
}

/** `aggregationData` merged with the root (Fansly puts joins in either). */
export function aggregation(p: unknown): Raw {
  const u = asObj(unwrap(p))
  return { ...u, ...asObj(u.aggregationData) }
}

export function byId(list: Raw[], key = 'id'): Map<string, Raw> {
  const m = new Map<string, Raw>()
  for (const r of list) {
    const id = str(r[key])
    if (id && !m.has(id)) m.set(id, r)
  }
  return m
}

// ── Money and time ──────────────────────────────────────────────────────────

/** Thousandths of a dollar → dollars. */
export const usd = (thousandths: unknown): number => num(thousandths) / 1000

/** Dollars → thousandths (what Fansly stores). */
export const thousandths = (dollars: number): number => Math.round(dollars * 1000)

export function formatUsd(dollars: number, lang = 'en'): string {
  try {
    return new Intl.NumberFormat(lang, { style: 'currency', currency: 'USD', minimumFractionDigits: dollars % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(dollars)
  } catch {
    return `$${dollars.toFixed(2)}`
  }
}

/** 75 → "1:15". */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Seconds or milliseconds → milliseconds (0 when absent). */
export function toMs(t: unknown): number {
  const n = num(t)
  if (!n) return 0
  return n < 1e11 ? n * 1000 : n
}

/** "5m", "3h", "2d" or a date after a week; '' for no time. */
export function relativeTime(ms: number, now = Date.now(), lang = 'en'): string {
  if (!ms) return ''
  const s = Math.max(0, Math.round((now - ms) / 1000))
  if (s < 60) return lang.startsWith('en') ? 'now' : '·'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d`
  return new Date(ms).toLocaleDateString(lang, { day: 'numeric', month: 'short', ...(new Date(ms).getFullYear() !== new Date(now).getFullYear() ? { year: 'numeric' } : {}) })
}

// ── Accounts ────────────────────────────────────────────────────────────────

export interface FanslyAccount {
  id: string
  username: string
  /** Display name, else @username, else ''. */
  name: string
  avatar: string
  banner: string
  about: string
  followsYou: boolean
  subscriber: boolean
  following: boolean
  raw: Raw
}

const ANONYMOUS = [/^\d+$/, /^user\d+$/i]

/** A usable display name, or '' (anonymous handles are skipped). */
export function nameOf(item: unknown): string {
  const o = asObj(item)
  const nested = ['user', 'fan', 'subscriber', 'profile', 'account'].map((k) => asObj(o[k]))
  const candidates = [o.partnerDisplayName, o.displayName, o.name, ...nested.map((n) => n.displayName ?? n.name)]
  for (const c of candidates) {
    const v = str(c).trim()
    if (v && !ANONYMOUS.some((re) => re.test(v))) return v
  }
  return ''
}

export function usernameOf(item: unknown): string {
  const o = asObj(item)
  return str(o.partnerUsername) || str(o.username) || ['user', 'fan', 'subscriber', 'profile', 'account'].map((k) => str(asObj(o[k]).username)).find(Boolean) || ''
}

export function accountOf(raw: unknown): FanslyAccount {
  const o = asObj(raw)
  const username = usernameOf(o)
  return {
    id: str(o.id) || str(o.accountId),
    username,
    name: nameOf(o) || username,
    avatar: mediaLocation(o.avatar, true),
    banner: mediaLocation(o.banner, false),
    about: str(o.about),
    followsYou: o.followsYou === true,
    subscriber: o.subscriber === true || !!o.subscription,
    following: o.following === true,
    raw: o,
  }
}

/** Accounts found anywhere a payload joins them (`accounts`, `aggregationData.accounts`). */
export function accountsIn(p: unknown): Map<string, FanslyAccount> {
  const agg = aggregation(p)
  const out = new Map<string, FanslyAccount>()
  for (const r of [...asList(agg.accounts), ...asList(asObj(unwrap(p)).accounts)]) {
    const a = accountOf(r)
    if (a.id && !out.has(a.id)) out.set(a.id, a)
  }
  return out
}

// ── Media ───────────────────────────────────────────────────────────────────

export type MediaKind = 'image' | 'video' | 'audio'

const AUDIO_EXT = /\.(mp3|m4a|aac|ogg|opus|wav|flac|oga)(\?|$)/i
const VIDEO_EXT = /\.(mp4|webm|mov|m3u8|mpd)(\?|$)/i

function locationsOf(m: Raw): string[] {
  const out: string[] = []
  for (const l of asList(m.locations)) {
    const v = str(l.location)
    if (/^https?:/i.test(v)) out.push(v)
  }
  const direct = str(m.location)
  if (/^https?:/i.test(direct)) out.push(direct)
  return out
}

/** The kind of one Fansly media/variant (by MIME first: variants reuse `type`). */
export function kindOf(m: unknown): MediaKind {
  const o = asObj(m)
  const mime = str(o.mimetype || o.mimeType).toLowerCase()
  if (mime.startsWith('audio/')) return 'audio'
  if (mime.startsWith('video/') || mime === 'application/vnd.apple.mpegurl') return 'video'
  if (mime.startsWith('image/')) return 'image'
  const url = locationsOf(o)[0] ?? str(o.filename)
  if (AUDIO_EXT.test(url)) return 'audio'
  if (VIDEO_EXT.test(url)) return 'video'
  const t = num(o.type)
  return t === 2 ? 'video' : t === 3 ? 'audio' : 'image'
}

/** Avatars/banners/thumbnails: the smallest (or largest) located variant, then the media itself. */
export function mediaLocation(media: unknown, preferSmall: boolean): string {
  if (typeof media === 'string') return /^https?:/i.test(media) ? media : ''
  const o = asObj(media)
  for (const k of ['tinyGifUrl', 'gifUrl', 'thumbnailUrl', 'previewUrl']) if (/^https?:/i.test(str(o[k]))) return str(o[k])
  if (/^https?:/i.test(str(o.url))) return str(o.url)
  const variants = asList(o.variants)
    .filter((v) => kindOf(v) === 'image' && locationsOf(v).length)
    .sort((a, b) => (preferSmall ? num(a.width) - num(b.width) : num(b.width) - num(a.width)))
  const nested = asObj(o.media)
  return (variants[0] && locationsOf(variants[0])[0]) || (kindOf(o) === 'image' ? locationsOf(o)[0] : '') || (Object.keys(nested).length ? mediaLocation(nested, preferSmall) : '') || ''
}

export interface Playable {
  /** The Fansly media id (the file, not the accountMedia). */
  mediaId: string
  kind: MediaKind
  /** The file to show/play; '' when the viewer has no access (locked) and nothing else is located. */
  src: string
  /** An image to show before play, or for a locked item. */
  poster: string
  width: number
  height: number
  /** Seconds (video/audio), 0 when unknown. */
  duration: number
}

function metaOf(m: Raw): Raw {
  try {
    return asObj(typeof m.metadata === 'string' ? JSON.parse(m.metadata) : m.metadata)
  } catch {
    return {}
  }
}

/** One Fansly media (file + variants) as something a page can show. */
export function playableMedia(media: unknown): Playable | null {
  const m = asObj(media)
  if (!Object.keys(m).length) return null
  const kind = kindOf(m)
  const files = [m, ...asList(m.variants)]
  const ofKind = files.filter((f) => kindOf(f) === kind && locationsOf(f).length).sort((a, b) => num(b.width) - num(a.width))
  const images = files.filter((f) => kindOf(f) === 'image' && locationsOf(f).length).sort((a, b) => num(b.width) - num(a.width))
  const src = (ofKind[0] && locationsOf(ofKind[0])[0]) || ''
  const poster = kind === 'image' ? src : (images[0] && locationsOf(images[0])[0]) || ''
  if (!src && !poster) return null
  const meta = metaOf(m)
  return {
    mediaId: str(m.id),
    kind,
    src,
    poster,
    width: num(m.width) || num(meta.originalWidth),
    height: num(m.height) || num(meta.originalHeight),
    duration: num(meta.duration),
  }
}

/** A sellable item (accountMedia) with its price and access, ready to show. */
export interface MediaItem {
  /** accountMedia id: what messages and posts attach (contentType 1). */
  id: string
  mediaId: string
  /** The full file, or null when locked to the viewer. */
  media: Playable | null
  /** The preview (blurred/teaser) Fansly shows when the file is locked. */
  preview: Playable | null
  /** Dollars; 0 = free. */
  price: number
  purchased: boolean
  /** The viewer (this account) can open it. */
  access: boolean
  likeCount: number
  createdAt: number
  raw: Raw
}

export function mediaItemOf(accountMedia: unknown): MediaItem {
  const am = asObj(accountMedia)
  const media = playableMedia(am.media)
  const preview = playableMedia(am.preview)
  const flags = asList(asObj(am.permissions).permissionFlags)
  const priced = flags.find((f) => num(f.price) > 0)
  return {
    id: str(am.id) || str(am.accountMediaId),
    mediaId: str(am.mediaId) || media?.mediaId || '',
    media,
    preview,
    price: usd(am.price) || usd(priced?.price),
    purchased: am.purchased === true,
    access: am.access !== false,
    likeCount: num(am.likeCount),
    createdAt: toMs(am.createdAt),
    raw: am,
  }
}

/** A bundle (contentType 2): several accountMedia sold together. */
export interface BundleItem {
  id: string
  items: MediaItem[]
  price: number
  purchased: boolean
  access: boolean
  preview: Playable | null
  raw: Raw
}

/** The accountMedia and bundles a payload carries (root, `aggregationData`, id-maps). */
export interface MediaCatalog {
  media: Map<string, Raw>
  bundles: Map<string, Raw>
}

export function catalogOf(...payloads: unknown[]): MediaCatalog {
  const media = new Map<string, Raw>()
  const bundles = new Map<string, Raw>()
  for (const p of payloads) {
    const u = asObj(unwrap(p))
    const agg = asObj(u.aggregationData)
    for (const src of [u.accountMedia, agg.accountMedia]) for (const r of asList(src)) media.set(str(r.id) || str(r.accountMediaId), r)
    for (const src of [u.accountMediaBundles, agg.accountMediaBundles]) for (const r of asList(src)) bundles.set(str(r.id) || str(r.bundleId), r)
  }
  media.delete('')
  bundles.delete('')
  return { media, bundles }
}

export function mergeCatalogs(a: MediaCatalog, b: MediaCatalog): MediaCatalog {
  return { media: new Map([...a.media, ...b.media]), bundles: new Map([...a.bundles, ...b.bundles]) }
}

export function bundleOf(raw: Raw, catalog: MediaCatalog): BundleItem {
  const ids = [
    ...asList(raw.bundleContent)
      .sort((a, b) => num(a.pos) - num(b.pos))
      .map((c) => str(c.accountMediaId)),
    ...(Array.isArray(raw.accountMediaIds) ? (raw.accountMediaIds as unknown[]).map(str) : []),
  ].filter(Boolean)
  const items = [...new Set(ids)].map((id) => catalog.media.get(id)).filter(Boolean).map((r) => mediaItemOf(r))
  const flags = asList(asObj(raw.permissions).permissionFlags)
  const priced = flags.find((f) => num(f.price) > 0)
  return {
    id: str(raw.id),
    items,
    price: usd(raw.price) || usd(priced?.price),
    purchased: raw.purchased === true,
    access: raw.access !== false,
    preview: playableMedia(raw.preview) ?? items[0]?.preview ?? items[0]?.media ?? null,
    raw,
  }
}

export type Attachment = { type: 'media'; item: MediaItem } | { type: 'bundle'; bundle: BundleItem }

/**
 * What a message or post attaches, resolved against the payload's catalog:
 * `attachments[{contentType, contentId}]` (1 = accountMedia, 2 = bundle) plus
 * the older single-id fields. Unresolvable stubs are dropped.
 */
export function attachmentsOf(item: unknown, catalog: MediaCatalog): Attachment[] {
  const o = asObj(item)
  const out: Attachment[] = []
  const seen = new Set<string>()
  const add = (type: number, id: string) => {
    if (!id || seen.has(`${type}:${id}`)) return
    seen.add(`${type}:${id}`)
    if (type === 2) {
      const b = catalog.bundles.get(id)
      if (b) out.push({ type: 'bundle', bundle: bundleOf(b, catalog) })
      return
    }
    const m = catalog.media.get(id)
    if (m) out.push({ type: 'media', item: mediaItemOf(m) })
  }
  for (const a of asList(o.attachments).sort((x, y) => num(x.pos) - num(y.pos))) {
    add(num(a.contentType) || 1, str(a.contentId) || str(a.accountMediaId) || str(a.id))
  }
  add(1, str(o.accountMediaId))
  add(2, str(o.accountMediaBundleId) || str(o.bundleId))
  return out
}

/** The first thing to show for an attachment (a bundle shows its first item). */
export function coverOf(a: Attachment): Playable | null {
  if (a.type === 'bundle') return a.bundle.preview
  return a.item.media ?? a.item.preview
}
